import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { faUpRightFromSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { captureMessage } from "@sentry/browser";
import cx from "classnames";
import { noop } from "lodash";
import { EditorView } from "prosemirror-view";

import { ReactComponent as LoomLogoWhite } from "~/legacy-assets/images/logos/loom-word-logo-white.svg";
import { ReactComponent as LoomLogo } from "~/legacy-assets/images/logos/loom-word-logo.svg";
import { insertLoomVideo } from "~/lib/blockInserter";
import { inCalendar, inVideocall } from "~/lib/browserExtension";
import { isDesktopApp, isTeams } from "~/lib/utils";
import { useRecordingState, useVideoRecording } from "~/lib/videoRecording";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { __globalModalContext } from "~/ui/modals";
import toast, { ToastActionButton } from "~/ui/toast";

import { getContext } from "../context";
import { createEmbedNode, type EmbedNodeAttributes } from "../integrations/helpers";

import styles from "./RecordVideoModal.module.css";

interface ModalProps {
	close: () => void;
	initialNoteContext: {
		id: string;
		url: string | null;
		streamName: string;
	};
	initialView: EditorView;
}

function ModalContent({ close, initialNoteContext, initialView }: ModalProps) {
	const history = useHistory();
	const isRecording = useRecordingState();
	const loom = useVideoRecording();
	const triggerRef = useRef<HTMLButtonElement>(null);

	const { id: initialNoteId, url: initialNoteUrl, streamName } = initialNoteContext;

	const goToNote = useCallback(
		() => history.push(`${initialNoteUrl}#${initialNoteId}`),
		[history, initialNoteUrl, initialNoteId],
	);

	useEffect(() => {
		if (isRecording) {
			toast.info("Please end this recording before starting another.", { title: "Recording in progress" });
			close();
		}
	}, [isRecording, close]);

	useLayoutEffect(() => {
		if (!triggerRef?.current) return;
		if (!loom || loom === "loading" || !loom.instance) return;

		loom.instance.configureButton({
			element: triggerRef.current,
			hooks: {
				// Not sure why these first two hooks are not optional, but it is what it is
				onRecordingComplete: noop,
				onUploadComplete: noop,
				onInsertClicked: loomVideo => {
					if (loomVideo) {
						if (!initialView.editable) {
							toast.error(
								<>
									Your video is still accessible from{" "}
									<a href={loomVideo.sharedUrl} target="_blank" rel="noopener noreferrer">
										Loom&apos;s website
										<FontAwesomeIcon
											icon={faUpRightFromSquare}
											size="sm"
											style={{ marginLeft: ".3ch", marginBottom: "1.5px" }}
										/>
									</a>
									.
								</>,
								{ title: "Note Locked", closeButton: true, autoClose: false },
							);
							return;
						}

						const embedAttrs: EmbedNodeAttributes = {
							service: "loom",
							title: loomVideo.title,
							embedUrl: loomVideo.embedUrl,
							embedId: loomVideo.id,
							originalUrl: loomVideo.sharedUrl,
						};

						try {
							/* Used as a check to see if the view has been destroyed
							 * If the view has been destroyed, this function call will throw
							 * TODO: find a more reliable way to know if the view has been destroyed
							 */
							initialView.nodeDOM(0);

							createEmbedNode(initialView, embedAttrs);
						} catch (error) {
							// Not in original stream, embedding via mutation...

							insertLoomVideo(initialNoteId, embedAttrs);
							toast.info(
								<>
									Your recording was embedded! Check it out in the series <strong>{streamName}</strong>.
								</>,
								{
									autoClose: false,
									closeButton: true,
									className: cx(styles.toast, styles.WithAction),
									actions:
										initialNoteUrl === null ? (
											<></>
										) : (
											<ToastActionButton onClick={goToNote}>Go to stream</ToastActionButton>
										),
								},
							);
						}
					} else {
						captureMessage("Undefined Loom video after filming.");
						toast.error("Something went wrong while embedding your Loom video.");
					}
				},
			},
		});
	}, [loom, initialView, initialNoteId, initialNoteUrl, goToNote, streamName]);

	if (isDesktopApp) {
		return (
			<p>
				This feature is only available in <strong>Google Chrome, Microsoft Edge, and Brave</strong> for the time being.
			</p>
		);
	}

	if (inVideocall || inCalendar) {
		return (
			<p>
				<strong>This feature is not available in the browser extension.</strong>
			</p>
		);
	}

	if (isTeams) {
		return (
			<p>
				<strong>This feature is not available in Microsoft Teams.</strong>
			</p>
		);
	}

	// Note: changed size from "medium" to "lg" as FontAwesome sizes differ from semantic-ui
	if (!loom || loom === "loading") return <LoadingSpinner className={styles.notLast} inline size="lg" />;

	switch (loom.support) {
		case "full":
			if (loom.instance === null)
				return (
					<p>
						<strong>
							Uh oh! We could not reach Loom.
							<br />
							Please refresh the page and try again.
						</strong>
					</p>
				);
			return (
				<Button size="wide" ref={triggerRef} variant="primary" onClick={close}>
					Record with Loom
				</Button>
			);

		case "blocked":
			return (
				<p>
					You will need to{" "}
					<a href="https://support.google.com/chrome/answer/95647?hl=en" target="_blank" rel="noopener noreferrer">
						enable third-party cookies
					</a>{" "}
					to use this feature. Then, refresh the page.
				</p>
			);

		case "unsupported":
			return (
				<p>
					This feature is only available in <strong>Google Chrome, Microsoft Edge, and Brave</strong> for the time
					being.
				</p>
			);
	}
}

function RecordVideoModal({ close, initialNoteContext, initialView }: ModalProps) {
	const theme = document.documentElement.dataset.theme;

	return (
		<Modal onClose={close} width="30rem">
			<Modal.Header onClose={close}>
				<Modal.Title>Record a video</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				{theme == "theme-dark" ? <LoomLogoWhite className={styles.logo} /> : <LoomLogo className={styles.logo} />}
				<p>Record a video effortlessly using Loom, then embed it directly into your Fellow note!</p>
				<ModalContent close={close} initialNoteContext={initialNoteContext} initialView={initialView} />
			</Modal.Body>
		</Modal>
	);
}

export default function openRecordVideoModal(initialEditorView: EditorView) {
	const context = getContext(initialEditorView.state);
	const initialNoteContext: ModalProps["initialNoteContext"] = {
		id: context.noteId,
		url: context.noteUrl,
		streamName: context.streamName,
	};

	__globalModalContext?.open<string | null>(({ close }) => (
		<RecordVideoModal close={close} initialNoteContext={initialNoteContext} initialView={initialEditorView} />
	));
}
